// InfoView.tsx — #info: the Austrian tax glossary, rendered verbatim (content is fixed —
// no invented tax claims, numbers or rules beyond what's given). Sections are
// <details>/<summary> accordions with a scroll-to table of contents.
//
// The TOC deliberately does NOT use real `<a href="#id">` anchors: this app's router
// (lib/router.ts) listens to every `hashchange` on `window`, and an in-page anchor like
// `#info-kest` would be parsed as an unknown route and bounced back to #cockpit. Instead
// each TOC entry is a button that scrolls the target into view and opens its <details>
// programmatically.
import { useRef } from 'preact/hooks';

interface Section {
  id: string;
  title: string;
  text: string;
}

const SECTIONS: Section[] = [
  {
    id: 'info-kest',
    title: 'KESt — Kapitalertragsteuer',
    text: 'In Österreich unterliegen Kapitalerträge einem KESt-Satz von 27,5 %. Besteuert werden realisierte Kursgewinne (beim Verkauf), Ausschüttungen und ausschüttungsgleiche Erträge. Eine Behaltefrist-Befreiung gibt es nicht mehr — die frühere Spekulationsfrist wurde 2011 abgeschafft. Nicht realisierte Buchgewinne bleiben steuerfrei, bis verkauft wird.',
  },
  {
    id: 'info-verkauf',
    title: 'Der Verkauf ist das steuerauslösende Ereignis',
    text: 'Besteuert wird beim Verkauf der Anteile, nicht bei der Entnahme vom Verrechnungskonto. Solange nicht verkauft wird, bleibt ein Kursgewinn unversteuert.',
  },
  {
    id: 'info-meldefonds',
    title: 'Meldefonds vs. Nicht-Meldefonds',
    text: 'Ein Meldefonds meldet seine steuerlichen Daten jährlich an die OeKB (Österreichische Kontrollbank); besteuert werden die tatsächlich gemeldeten Erträge. Nicht-Meldefonds unterliegen einer Pauschalbesteuerung: jährlich wird der größere Wert aus 90 % des Wertzuwachses und 10 % des Jahresendwerts als Ertrag angesetzt und mit 27,5 % besteuert. Die Pauschalbesteuerung ist typischerweise ungünstiger — die allermeisten in Österreich gehandelten ETFs sind Meldefonds.',
  },
  {
    id: 'info-oekb-meldung',
    title: 'Was ist eine OeKB-Meldung?',
    text: 'Ein Meldefonds übermittelt seine steuerlich relevanten Kennzahlen pro Anteil — u. a. die ausschüttungsgleichen Erträge (AGE), die Korrektur der Anschaffungskosten und die anrechenbare KESt — an die OeKB (Österreichische Kontrollbank): thesaurierende Fonds einmal pro Geschäftsjahr, ausschüttende meist einmal je Ausschüttung. Dieses Cockpit cached diese Meldungen lokal und braucht sie, um die AGE-Historie und die Anschaffungskosten-Basis eines Fonds korrekt nachzubilden (Historie-Ansicht, „Ausschüttungsgleiche Erträge/Jahr") — fehlt eine Meldung, bleibt die betreffende Auswertung sichtbar unvollständig statt geschätzt.',
  },
  {
    id: 'info-age',
    title: 'Ausschüttungsgleiche Erträge (AGE)',
    text: 'Bei thesaurierenden Meldefonds werden die im Fonds einbehaltenen, nicht ausgeschütteten Erträge trotzdem jährlich besteuert — die ausschüttungsgleichen Erträge. Die KESt darauf fällt an, ohne dass etwas verkauft oder ausgezahlt wird. Die Bank bucht sie vom Verrechnungskonto ab.',
  },
  {
    id: 'info-verrechnungskonto',
    title: 'Das Verrechnungskonto kann negativ werden',
    text: 'Weil die KESt auf ausschüttungsgleiche Erträge abgebucht wird, ohne dass Geld zufließt, kann das Verrechnungskonto ins Minus rutschen. Das ist korrektes Verhalten, kein Fehler — das Cockpit bildet es bewusst so ab.',
  },
  {
    id: 'info-anschaffungskosten',
    title: 'Erhöhung der Anschaffungskosten (der zentrale Mechanismus)',
    text: 'Bereits versteuerte ausschüttungsgleiche Erträge erhöhen die Anschaffungskosten, also die steuerliche Kostenbasis. Beim späteren Verkauf wird nur der Gewinn über dieser erhöhten Basis besteuert. So wird derselbe Ertrag nicht zweimal besteuert. Diese Basis-Erhöhung korrekt abzubilden ist die Kernaufgabe dieses Rechners.',
  },
  {
    id: 'info-thesaurierend',
    title: 'Thesaurierend vs. ausschüttend',
    text: 'Bei ausschüttenden Fonds wird der Ertrag ausgezahlt — am Ex-Tag fällt der Kurs entsprechend — und sofort mit 27,5 % besteuert; die Anschaffungskosten bleiben unverändert. Bei thesaurierenden Fonds bleibt der Ertrag im Fonds, wird als AGE besteuert und erhöht die Anschaffungskosten. Wirtschaftlich führt beides bei sonst gleichen Annahmen zur selben Gesamtsteuer — nur Zeitpunkt und Ort des Geldflusses unterscheiden sich.',
  },
  {
    id: 'info-substanzauszahlungen',
    title: 'Substanzauszahlungen',
    text: 'Auszahlungen aus der Fondssubstanz (Kapitalrückzahlung) sowie Auszahlungen bereits versteuerter Erträge sind bei Auszahlung steuerfrei und senken die Anschaffungskosten. Der netto gemeldete Korrekturbetrag der OeKB kann daher auch negativ sein.',
  },
  {
    id: 'info-ter',
    title: 'TER vs. Ausführungsgebühr — und der Break-even',
    text: 'Die Ausführungsgebühr ist ein fixer Euro-Betrag pro Sparplan-Ausführung; als Prozentsatz der Rate wirkt sie anfangs stark, wächst aber nur linear. Die TER (jährliche Fondskosten) ist ein Prozentsatz des Depotwerts und wächst mit dem Vermögen. Ab einem bestimmten Zeitpunkt übersteigen die kumulierten TER-Kosten die kumulierten Ausführungsgebühren — dieses Break-even-Datum weist das Cockpit gesondert aus. Wichtig: Bei Backtests mit echten Kursen ist die TER bereits im Kurs enthalten und wird nicht noch einmal abgezogen; in der Kostenaufstellung erscheint sie dort nur informativ und ausgegraut.',
  },
  {
    id: 'info-datenquellen',
    title: 'Datenquellen & Genauigkeit',
    text: 'Steuerdaten (ausschüttungsgleiche Erträge, Korrekturbeträge) stammen von der OeKB; Kurse und Ausschüttungen von Yahoo Finance; Wechselkurse von der EZB. Viele irische ETFs (etwa von iShares oder Vanguard) melden ihre steuerlichen Kennzahlen in US-Dollar — diese werden zum jeweiligen Meldungs- bzw. Zuflusstag in Euro umgerechnet. Liegen für einen Fonds keine Steuerdaten vor, wird die betreffende Funktion sichtbar deaktiviert und nicht geschätzt.',
  },
  {
    id: 'info-nicht-abgebildet',
    title: 'Was dieser Rechner (noch) nicht abbildet',
    text: 'Teilverkäufe (gleitender Durchschnittspreis über die volle Position hinaus), Verlustausgleich zwischen Positionen, Altbestand vor 2011, Mehrfach-Depots und Sollzinsen auf ein negatives Verrechnungskonto sind bewusst nicht modelliert.',
  },
];

export function InfoView() {
  const detailsRefs = useRef<Record<string, HTMLDetailsElement | null>>({});

  function goToSection(id: string) {
    const el = detailsRefs.current[id];
    if (!el) return;
    el.open = true;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  return (
    <div class="info-page">
      <div class="card info-intro">
        <h2>Info &amp; Steuer — so rechnet dieses Cockpit</h2>
        <div class="banner banner-info" role="status">
          <strong>Kein Ersatz für Steuerberatung</strong>
          <span>
            Dieses Cockpit ist ein privates Modell zur Orientierung. Maßgeblich für die
            tatsächliche Besteuerung sind die Meldedaten des Fonds sowie dein
            Steuerberater bzw. das Finanzamt.
          </span>
        </div>
      </div>

      <div class="card info-toc-card">
        <div class="section-title">Inhalt</div>
        <nav class="info-toc" aria-label="Abschnitte">
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" onClick={() => goToSection(s.id)}>
              {s.title}
            </button>
          ))}
        </nav>
      </div>

      <div class="card info-content-card">
        {SECTIONS.map((s, i) => (
          <details
            key={s.id}
            id={s.id}
            class="details-section"
            open={i === 0}
            ref={(el) => {
              detailsRefs.current[s.id] = el;
            }}
          >
            <summary>{s.title}</summary>
            <div class="section-body">
              <p>{s.text}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
